import { createAction, props } from '@ngrx/store';
import {
  ChatFavoriteSection,
  ChatBootstrapData,
  ChatChannelUpdatedData,
  ChatDiscoveredChannel,
  ChatDiscoverMeta,
  ChatNotificationLevelEnum,
  ChatPinUpdatedData,
  ChatPresenceUpdatedData,
  ChatReadUpdatedData,
  ChatSavedMessage,
  ChatStatusUpdatedData,
  ChatUnreadUpdatedData,
  ChatUserDisplayMap,
  ChatUserSetting,
} from 'lib-trend-core';
import { ChatComposerDraft, ChatConnectionStatus } from './chat.state';

// Internal Chat store actions (SPEC-2026-07-internal-chat, T-18) — the
// attendance-panel get*/set* naming convention. Socket fan-out is bridged into
// `apply*` actions by ChatSocketBridgeService (never subscribed inside
// components other than the open-conversation local append).

// bootstrap ------------------------------------------------------------------
export const loadBootstrap = createAction('[Chat] Load Bootstrap');
export const setBootstrap = createAction('[Chat] Set Bootstrap', props<{ data: ChatBootstrapData }>());
/**
 * `status` is the HTTP status of the failed read (0/absent on a transport
 * failure) — the reducer classifies it into `bootstrapErrorKind` so the
 * surfaces can tell "chat backend unreachable" from "request refused" (T-25).
 */
export const setBootstrapFailure = createAction('[Chat] Set Bootstrap Failure', props<{ error: string, status?: number }>());

// discover / saved -----------------------------------------------------------
/**
 * `GET /api/chat/channels/discover` — the public-channel directory (RF-03).
 * `page`/`perPage` follow the endpoint meta; `append` keeps the pages already
 * rendered instead of replacing them (the browse dialog's "load more").
 */
export const discoverChannels = createAction(
  '[Chat] Discover Channels',
  props<{ search?: string, page?: number, perPage?: number, append?: boolean }>(),
);
export const setDiscoveredChannels = createAction(
  '[Chat] Set Discovered Channels',
  props<{ channels: ChatDiscoveredChannel[], meta?: ChatDiscoverMeta, append?: boolean }>(),
);
/**
 * CHANNEL_JOIN acked — flip the browsed row to "joined" immediately. The
 * conversation itself arrives through the `joined` fan-out + bootstrap refresh;
 * this only keeps the open directory list honest.
 */
export const applyDiscoveredJoin = createAction('[Chat] Apply Discovered Join', props<{ channelId: string }>());
export const loadSaved = createAction('[Chat] Load Saved');
/** `GET /api/chat/saved` landed — `userDisplay` comes from the envelope meta. */
export const setSaved = createAction('[Chat] Set Saved', props<{ saved: ChatSavedMessage[], userDisplay?: ChatUserDisplayMap }>());

// open conversation ----------------------------------------------------------
export const openConversation = createAction('[Chat] Open Conversation', props<{ channelId: string }>());
export const openDirectDraft = createAction('[Chat] Open Direct Draft', props<{ userId: string, directKey: string }>());
export const closeConversation = createAction('[Chat] Close Conversation');

// socket bridge --------------------------------------------------------------
export const applyUnreadUpdate = createAction('[Chat Socket] Apply Unread Update', props<{ update: ChatUnreadUpdatedData }>());
export const applyChannelUpdate = createAction('[Chat Socket] Apply Channel Update', props<{ update: ChatChannelUpdatedData, currentUserId: string }>());
export const applyPresenceUpdate = createAction('[Chat Socket] Apply Presence Update', props<{ update: ChatPresenceUpdatedData }>());
/**
 * DIRECT read watermark of the counterpart (RF-33/AC-29). `currentUserId` lets
 * the reducer drop my own read echo — only the OTHER side moves the indicator.
 */
export const applyReadUpdate = createAction('[Chat Socket] Apply Read Update', props<{ update: ChatReadUpdatedData, currentUserId: string }>());
export const applyStatusUpdate = createAction('[Chat Socket] Apply Status Update', props<{ update: ChatStatusUpdatedData }>());
export const applyPinUpdate = createAction('[Chat Socket] Apply Pin Update', props<{ update: ChatPinUpdatedData }>());

// optimistic local updates (server truth follows on the socket) --------------
/** Own outbound message accepted — refresh the conversation preview/activity. */
export const setConversationPreview = createAction('[Chat] Set Conversation Preview', props<{ channelId: string, preview: string }>());
/** NOTIFICATION_SET_LEVEL dispatched — reflect immediately on the row. */
export const setNotificationLevel = createAction('[Chat] Set Notification Level', props<{ channelId: string, level: ChatNotificationLevelEnum }>());
/** MARK_UNREAD dispatched — force the badge to at least 1 immediately. */
export const setManualUnread = createAction('[Chat] Set Manual Unread', props<{ channelId: string }>());
/** READ dispatched — clear the badge immediately. */
export const clearUnread = createAction('[Chat] Clear Unread', props<{ channelId: string }>());

// message actions (T-20) -------------------------------------------------------
/**
 * SAVED_TOGGLE dispatched — flip the bookmark immediately. Saving is personal
 * (no fan-out), so the optimistic value IS the visible truth until the next
 * `loadSaved` refresh confirms it.
 */
export const applySavedToggle = createAction('[Chat] Apply Saved Toggle', props<{ messageId: string, saved: boolean }>());
/** Thread panel opened/refreshed — record the reply count I have now seen. */
export const setThreadSeen = createAction('[Chat] Set Thread Seen', props<{ messageId: string, count: number }>());
/**
 * Adds authors resolved by a secondary read (saved list, thread page) to the
 * shared display map — merge only, an existing entry is never dropped.
 */
export const mergeUserDisplay = createAction('[Chat] Merge User Display', props<{ userDisplay: ChatUserDisplayMap }>());

// composer drafts (T-19) -------------------------------------------------------
/** Persist the composer draft of a conversation (key = channelId | `direct:{userId}`). */
export const setComposerDraft = createAction('[Chat] Set Composer Draft', props<{ key: string, draft: ChatComposerDraft }>());
/** Drop a persisted draft (sent or explicitly emptied). */
export const clearComposerDraft = createAction('[Chat] Clear Composer Draft', props<{ key: string }>());

// status & preferences (T-21) --------------------------------------------------
/**
 * STATUS_SET / STATUS_CLEAR / SETTINGS_UPDATE dispatched — reflect my own
 * settings document immediately (RF-29/RF-36). The authoritative availability
 * and custom status also arrive on `chat:status:updated` (the broadcast
 * includes me), but quiet hours and the sound flag are NOT broadcast, so this
 * patch is the only live source between two bootstraps. Rolled back with the
 * previous snapshot when the command acks an error.
 */
export const patchMySettings = createAction('[Chat] Patch My Settings', props<{ patch: Partial<ChatUserSetting> }>());
/**
 * Favourite sections broadcast after a change on another connection of the same user
 * (SPEC-2026-09-internal-chat-pwa, RF-23) — replaces the list the bootstrap loaded.
 */
export const setFavoriteSections = createAction('[Chat] Set Favorite Sections', props<{ favoriteSections: ChatFavoriteSection[] }>());

// interface states (T-25) ------------------------------------------------------
/** Websocket link state pushed by ChatConnectionService (banner source). */
export const setConnectionStatus = createAction('[Chat UI] Set Connection Status', props<{ status: ChatConnectionStatus }>());
/** The brief "Conectado" bar has been shown long enough — hide it. */
export const clearConnectionRestored = createAction('[Chat UI] Clear Connection Restored');

// failure --------------------------------------------------------------------
export const setFailure = createAction('[Chat] Failure', props<{ error: string }>());
